import {
    MutationHookOptions,
    useMutation,
} from '/@utils/error-handling/apollo-hooks-wrappers/custom-apollo-hooks';
import { invalidateSessionInfoCallCache } from '/@utils/service-worker.util';
import {
    Exact,
    UpdateSeenStateDocument,
    UpdateSeenStateMutation,
    UpdateSeenStateMutationVariables,
    UpdateUserPersonaDocument,
    UpdateUserPersonaMutation,
    UpdateUserPersonaMutationVariables,
    UpdateUserPreferencesDocument,
    UpdateUserPreferencesMutation,
    UpdateUserPreferencesMutationVariables,
} from '../generated/graphql-types';

// declare mutation hook options type.
type mutationHookOptionsType = MutationHookOptions<
    UpdateUserPreferencesMutation,
    Exact<{ userId: string; userPreferences: string }>,
    any
>;

export const useUpdateSeenStateMutation = () => {
    const [updateSeenState, { data, error, loading }] = useMutation<
        UpdateSeenStateMutation,
        UpdateSeenStateMutationVariables
    >(UpdateSeenStateDocument);

    return {
        data: data?.User__updateSeenState,
        error,
        loading,
        updateSeenState: (props: {
            hasSeenOnboarding?: boolean;
            hasSeenWorksheetTour?: boolean;
            hasSeenAutoAnswerTour?: boolean;
        }) =>
            updateSeenState({
                variables: {
                    hasSeenOnboarding: props.hasSeenOnboarding,
                    hasSeenWorksheetTour: props.hasSeenWorksheetTour,
                    hasSeenAutoAnswerTour: props.hasSeenAutoAnswerTour,
                },
            }),
    };
};

export const useUpdateUserPreferences = (
    options: mutationHookOptionsType = {},
) => {
    const [updateUserPreferences, { data, error, loading }] = useMutation<
        UpdateUserPreferencesMutation,
        UpdateUserPreferencesMutationVariables
    >(UpdateUserPreferencesDocument, options);

    return {
        data: data?.User__updateUserPreferences,
        error,
        loading,
        updateUserPreferences: (
            userId: string,
            userPreferences: string,
            shouldInvalidateCache = true,
        ) => {
            if (shouldInvalidateCache) {
                invalidateSessionInfoCallCache();
            }
            return updateUserPreferences({
                variables: {
                    userId,
                    userPreferences,
                },
            });
        },
    };
};

export const useUpdateUserPersona = () => {
    const [updateUserPersona, { data, error, loading }] = useMutation<
        UpdateUserPersonaMutation,
        UpdateUserPersonaMutationVariables
    >(UpdateUserPersonaDocument);

    return {
        data: data?.User__updateUserPersona,
        error,
        loading,
        updateUserPersona: (userpersona: string) =>
            updateUserPersona({
                variables: {
                    userpersona,
                },
            }),
    };
};
